import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';

const DataExportSection = () => {
  const [selectedData, setSelectedData] = useState({
    interviewHistory: true,
    performanceReports: true,
    personalInfo: false,
    mentorConversations: false,
    courseProgress: false
  });

  const [isExporting, setIsExporting] = useState(false);

  const dataOptions = [
    {
      id: 'interviewHistory',
      label: 'Interview History',
      description: 'All your completed interview sessions with questions and responses',
      icon: 'FileText',
      size: '2.4 MB'
    },
    {
      id: 'performanceReports',
      label: 'Performance Reports',
      description: 'Detailed analytics, scores, and improvement trends',
      icon: 'BarChart3',
      size: '1.8 MB'
    },
    {
      id: 'personalInfo',
      label: 'Personal Information',
      description: 'Profile data, preferences, and account settings',
      icon: 'User',
      size: '0.2 MB'
    },
    {
      id: 'mentorConversations',
      label: 'Mentor Conversations',
      description: 'Chat history and consultation notes with mentors',
      icon: 'MessageSquare',
      size: '0.8 MB'
    },
    {
      id: 'courseProgress',
      label: 'Course Progress',
      description: 'Completed courses and learning materials',
      icon: 'BookOpen',
      size: '1.2 MB'
    }
  ];

  const handleToggle = (id) => {
    setSelectedData(prev => ({ ...prev, [id]: !prev?.[id] }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    Object.keys(selectedData)?.forEach(key => {
      allSelected[key] = true;
    });
    setSelectedData(allSelected);
  };

  const handleDeselectAll = () => {
    const allDeselected = {};
    Object.keys(selectedData)?.forEach(key => {
      allDeselected[key] = false;
    });
    setSelectedData(allDeselected);
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
    }, 2000);
  };

  const getTotalSize = () => {
    let total = 0;
    dataOptions?.forEach(option => {
      if (selectedData?.[option?.id]) {
        total += parseFloat(option?.size);
      }
    });
    return total?.toFixed(1);
  };

  const getSelectedCount = () => {
    return Object.values(selectedData)?.filter(Boolean)?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Download" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">
              Data Export
            </h2>
            <p className="text-sm text-muted-foreground">
              Download your interview data and reports
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">
                About Data Export
              </h3>
              <p className="text-sm text-muted-foreground">
                Your data will be exported in JSON format. The download may take a few minutes depending on the amount of data selected. All exported data is encrypted for your security.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pb-4 border-b border-border">
          <div>
            <p className="text-sm font-medium text-foreground">
              {getSelectedCount()} of {dataOptions?.length} items selected
            </p>
            <p className="text-xs text-muted-foreground">
              Total size: {getTotalSize()} MB
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeselectAll}
            >
              Deselect All
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {dataOptions?.map((option) => (
            <div
              key={option?.id}
              className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors duration-150 ease-out"
            >
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <Icon name={option?.icon} size={20} color="var(--color-muted-foreground)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-foreground mb-1">
                      {option?.label}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      {option?.description}
                    </p>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                      {option?.size}
                    </span>
                  </div>
                  <Checkbox
                    checked={selectedData?.[option?.id]}
                    onChange={(e) => handleToggle(option?.id)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={handleExport}
            loading={isExporting}
            disabled={getSelectedCount() === 0}
          >
            {isExporting ? 'Preparing Export...' : 'Export Selected Data'}
          </Button>
          <Button
            variant="outline"
            iconName="Mail"
            iconPosition="left"
          >
            Email Export Link
          </Button>
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Clock" size={20} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-foreground mb-1">
                Recent Exports
              </h3>
              <div className="space-y-2 mt-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Full Data Export</span>
                  <span className="text-foreground">10/28/2025</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Performance Reports</span>
                  <span className="text-foreground">09/15/2025</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExportSection;